import type {
  CryptoAlertData,
  PositionData,
  UserPosition,
} from "@crypto-trading/common/browser";
import type { LivePriceExchangeCode } from "@/hooks/useTokenLivePrice";

/** Every position the user opened for the alert (power autotrade can open several). */
export function alertPositions(alert: CryptoAlertData): UserPosition[] {
  if (alert.userPositions && alert.userPositions.length > 0) {
    return alert.userPositions;
  }
  return alert.userPosition ? [alert.userPosition] : [];
}

export function isOpenPosition(position: UserPosition): boolean {
  return position.status === "OPEN";
}

/**
 * PnL % on margin. Closed positions use the stored value; open ones are
 * estimated from the live price when there is one (fees not included).
 */
export function positionPnlPercent(
  position: UserPosition,
  livePrice?: number | null
): number | null {
  if (isOpenPosition(position) && livePrice && position.entryPrice) {
    const move = (livePrice - position.entryPrice) / position.entryPrice;
    const direction = position.positionType === "SHORT" ? -1 : 1;
    return move * direction * position.leverage * 100;
  }
  return position.pnlPercentage ?? null;
}

/** The position with the highest PnL %. Unknown PnL counts as 0 %. */
export function pickBestPosition(
  positions: UserPosition[],
  livePrice?: number | null
): UserPosition | null {
  let best: UserPosition | null = null;
  let bestPnl = -Infinity;
  for (const position of positions) {
    const pnl = positionPnlPercent(position, livePrice) ?? 0;
    if (!best || pnl > bestPnl) {
      best = position;
      bestPnl = pnl;
    }
  }
  return best;
}

export function liveExchangeFor(position: UserPosition): LivePriceExchangeCode {
  const code = (position.exchangeCode || position.exchangeName || "").toLowerCase();
  if (code.includes("binance")) return "binance";
  if (code.includes("bybit")) return "bybit";
  if (code.includes("gate")) return "gate";
  if (code.includes("kucoin")) return "kucoin";
  if (code.includes("aster")) return "asterdex";
  if (code.includes("hyperliquid")) return "hyperliquid";
  return "dynamic";
}

export function exchangeLabel(position: UserPosition): string {
  if (position.exchangeName) return position.exchangeName;
  if (!position.exchangeCode) return "Exchange";
  return position.exchangeCode[0].toUpperCase() + position.exchangeCode.slice(1);
}

/** Shape the position detail drawer expects. */
export function toDrawerPosition(position: UserPosition): PositionData {
  return {
    positionId: position.id,
    symbol: position.symbol,
    positionType: position.positionType as "LONG" | "SHORT",
    amount: position.amount.toString(),
    leverage: position.leverage.toString(),
    entryPrice: position.entryPrice.toString(),
    markPrice: position.markPrice?.toString() || "0",
    exitPrice: position.exitPrice?.toString() || "0",
    pnl: position.realizedPnl?.toString() || "0",
    pnlPercentage: position.pnlPercentage?.toString() || "0",
    exchangeCode: position.exchangeCode ?? undefined,
    exchangeName: position.exchangeName ?? undefined,
    usdAmount: position.usdAmount.toString(),
    openedAt: position.openedAt?.toString() || undefined,
    closedAt: position.closedAt?.toString() || undefined,
  };
}
